import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandS,
  BrandXS,
  BrandXXS,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { ContenderBonus } from 'components/lineup/Contender/ContenderFooter/ContenderBonus';
import { DumbStatus } from 'components/managerTeam/Status/DumbStatus';
import { colorPerBonusRating } from 'lib/bonus';

import type { RankRewardsRanking_so5Ranking } from './__generated__/index.graphql';

const Root = styled.div`
  position: relative;
  display: flex;
  gap: var(--triple-unit);
  justify-content: space-between;
  align-items: start;
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
  padding: var(--padding, var(--intermediate-unit));

  &.isMyRanking {
    --border-gradient: linear-gradient(
      90deg,
      #294bff 0%,
      rgba(41, 75, 255, 0.3) 100%
    );
    background: linear-gradient(
        90deg,
        rgba(41, 75, 255, 0.3) 0%,
        rgba(0, 0, 0, 0) 100%
      ),
      rgba(255, 255, 255, 0.11);
  }

  &.rank-1 {
    --border-gradient: var(--c-gradient-gold);
    background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.11),
        rgba(255, 255, 255, 0.11)
      ),
      linear-gradient(90deg, rgba(212, 150, 31, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
  }
  &.rank-2 {
    --border-gradient: var(--c-gradient-silver);
    background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.11),
        rgba(255, 255, 255, 0.11)
      ),
      linear-gradient(90deg, rgba(214, 228, 233, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
  }
  &.rank-3 {
    --border-gradient: var(--c-gradient-bronze);
    background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.11),
        rgba(255, 255, 255, 0.11)
      ),
      linear-gradient(90deg, rgba(246, 158, 93, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
  }

  &.isSelected {
    --border-gradient: linear-gradient(
      90deg,
      #294bff 0%,
      rgba(41, 75, 255, 0.3) 100%
    );
    background: linear-gradient(
        90deg,
        rgba(41, 75, 255, 0.3) 0%,
        rgba(0, 0, 0, 0) 100%
      ),
      rgba(255, 255, 255, 0.11);
  }

  &.rank-1,
  &.rank-2,
  &.rank-3,
  &.isMyRanking,
  &.isSelected {
    ${borderGradientStyle}
  }
`;

const Score = styled(BrandS)`
  display: inline-flex;
  gap: 2px;
`;

const StyledButtonBase = styled(ButtonBase)`
  width: 100%;
  text-align: left;
`;

const AvatarContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  border-radius: var(--quadruple-unit);
  background-color: var(--c-nd-400);

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Infos = styled(Vertical)`
  overflow: hidden;
`;

const RankingContainer = styled(BrandXXS)`
  position: absolute;
  right: 0;
  bottom: 0;
  padding: 4px 6px 4px 6px;
  border-radius: var(--unit) 0px var(--double-unit) 0px;
  background: var(--c-nd-100);

  .rank-1 & {
    background: var(--c-gradient-gold);
  }
  .rank-2 & {
    background: var(--c-gradient-silver);
  }
  .rank-3 & {
    background: var(--c-gradient-bronze);
  }
  .isMyRanking & {
    background: var(--c-white);
  }
  .rank-1 &,
  .rank-2 &,
  .rank-3 &,
  .isMyRanking & {
    color: var(--c-black);
  }
  .isSelected &,
  .isMyRanking & {
    color: var(--c-white);
    background: var(--c-blue-400);
  }
`;

const StatusContainer = styled.div`
  position: absolute;
  display: flex;
  left: 0;
  top: 0;
  transform: translate(-3px, -3px);
`;

const Bonus = styled(LabelS)`
  display: flex;
  align-items: center;
  gap: 2px;
`;

type Props = {
  so5Ranking: RankRewardsRanking_so5Ranking;
  isMyRanking?: boolean;
  onClick?: () => void;
  children?: ReactNode;
  displayRank?: boolean;
  isSelected?: boolean;
};

export const Ranking = ({
  so5Ranking,
  isMyRanking,
  isSelected,
  children,
  displayRank = true,
  onClick,
}: Props) => {
  const { user } = so5Ranking.so5Lineup;

  const bonusColor = so5Ranking.so5Lineup.bonusRating
    ? colorPerBonusRating[so5Ranking.so5Lineup.bonusRating]
    : 'var(--c-nd-600)';

  return (
    <Root
      key={so5Ranking.id}
      as={onClick ? StyledButtonBase : undefined}
      onClick={onClick}
      style={
        {
          '--padding':
            !!children && displayRank
              ? 'var(--intermediate-unit) var(--intermediate-unit) var(--quadruple-unit) var(--intermediate-unit)'
              : undefined,
        } as CSSProperties
      }
      className={classNames(
        so5Ranking.eligibleDivisionChange,
        `rank-${so5Ranking.ranking}`,
        {
          isMyRanking,
          isSelected,
        }
      )}
    >
      <Vertical gap={1.5}>
        <Horizontal gap={1.5}>
          <AvatarContainer>
            {!!so5Ranking.eligibleDivisionChange &&
              so5Ranking.eligibleDivisionChange !== DivisionChange.STABLE && (
                <StatusContainer>
                  <DumbStatus
                    divisionChange={so5Ranking.eligibleDivisionChange}
                    size={2}
                  />
                </StatusContainer>
              )}
            <Avatar user={user} rounded size={5} />
          </AvatarContainer>
          <Infos gap={0.5}>
            <LabelL bold>
              <Ellipsis>{user.nickname}</Ellipsis>
            </LabelL>
            <Horizontal>
              {so5Ranking.so5Lineup.cancelledAt ? (
                <BrandXS color="var(--c-yellow-400)">
                  <FormattedMessage
                    id="RankRewards.Ranking.canceled"
                    defaultMessage="Canceled lineup"
                  />
                </BrandXS>
              ) : (
                <>
                  <Score>
                    <FormattedMessage
                      {...fantasy.ordinalPoints}
                      values={{
                        score: so5Ranking.score,
                        sup: (...chunks: ReactNode[]) => (
                          <BrandXXS as="sup" color="var(--c-nd-600)" bold>
                            {chunks}
                          </BrandXXS>
                        ),
                      }}
                    />
                  </Score>
                  {!!so5Ranking.so5Lineup.bonusPoints && (
                    <Bonus bold color={bonusColor}>
                      <ContenderBonus
                        bonusRating={so5Ranking.so5Lineup.bonusRating}
                        bonus={so5Ranking.so5Lineup.bonusPoints}
                      />
                    </Bonus>
                  )}
                </>
              )}
            </Horizontal>
          </Infos>
        </Horizontal>
        {!!displayRank && (
          <RankingContainer as="p" bold>
            <FormattedMessage
              {...glossary.ordinal}
              values={{
                ordinal: so5Ranking.so5Lineup.cancelledAt
                  ? '-'
                  : so5Ranking.ranking ?? 0,
              }}
            />
          </RankingContainer>
        )}
        {children}
      </Vertical>
    </Root>
  );
};

Ranking.fragments = {
  so5Ranking: gql`
    fragment RankRewardsRanking_so5Ranking on So5Ranking {
      id
      eligibleDivisionChange
      ranking
      score
      so5Lineup {
        id
        name
        bonusPoints
        bonusRating
        cancelledAt
        managerTeam {
          id
          name
        }
        user {
          slug
          nickname
          ...Avatar_publicUserInfoInterface
        }
      }
    }
    ${Avatar.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<RankRewardsRanking_so5Ranking>,
};
